import { FeesRepo } from "@services/fees.repo";
import { AuthService } from "@common/auth.service";
import { Roles } from "@common/enums/Roles";
import { TableColumn } from "@models/components/tableColumn";
import * as moment from "moment";
import angular = require("angular");
import { extractErrorMessage } from "@common/utils/errorUtils";

export class ExclusionsController {
  public allExclusions: Array<any> = [];
  public isLoading: boolean = false;
  public client: any;
  public onRefresh: () => void;
  public canEdit: boolean = true;
  private portfolioDefaults: { [portfolioId: number]: string } = {};

  public exclusionColumns: TableColumn[] = [
    { key: 'portfolioName', label: 'Portfolio', type: 'text', width: 'col-md-2' },
    { key: 'securityDisplay', label: 'Security', type: 'text', width: 'col-md-2' },
    { key: 'marketValueDisplay', label: 'Market Value', type: 'html', width: 'col-md-1' },
    { key: 'fromDateDisplay', label: 'Start Date', type: 'text', width: 'col-md-1' },
    { key: 'toDateDisplay', label: 'End Date', type: 'html', width: 'col-md-1' },
    { key: 'isActive', label: 'Active', type: 'boolean', width: 'col-md-1' },
    { key: 'comment', label: 'Comment', type: 'text', width: 'col-md-2' }
  ];

  /** @ngInject */
  constructor(
    private feesRepo: FeesRepo,
    private authService: AuthService,
    private notifyjs: any,
    private $mdDialog: angular.material.IDialogService,
    private $rootScope: angular.IRootScopeService
  ) {}

  $onInit = () => {
    if (this.client && this.client.clientNumber) {
      this.getClientFeeExclusions();
    }
  };

  $onChanges = (changes: any) => {
    if (changes.client && !changes.client.isFirstChange() && changes.client.currentValue && changes.client.currentValue.clientNumber) {
      this.getClientFeeExclusions();
    }
  };

  convertToLocaleTime = (date: any): string => {
    if (!date) return '';
    return moment(new Date(date)).format("DD-MM-YYYY");
  };

  getClientFeeExclusions = () => {
    if (!this.client || !this.client.clientNumber) return;
    this.isLoading = true;
    this.allExclusions = [];
    this.feesRepo
      .getClientFeeExclusionsByClientNumber(this.client.clientNumber)
      .then((res: any) => {
        const data = res.data || {};
        this.allExclusions = this.formatExclusionsForDisplay(data.exclusions || []);
        this.portfolioDefaults = {};
        (data.portfolioDefaults || []).forEach((pd: any) => {
          this.portfolioDefaults[pd.portfolioId] = pd.defaultCurrency;
        });
      })
      .catch((err) => {
        this.notifyjs("Failed to load Fee Exclusions " + extractErrorMessage(err));
      })
      .finally(() => {
        this.isLoading = false;
      });
  };

  formatExclusionsForDisplay = (exclusions: Array<any>): Array<any> => {
    return exclusions.map((exclusion: any) => {
      const formatted = { ...exclusion };

      // Security display - show securityName or "Cash exclusion"
      formatted.securityDisplay = exclusion.isCashExclusion ? 'Cash exclusion' : exclusion.securityName;

      // Market Value display
      if (exclusion.isCashExclusion) {
        formatted.marketValueDisplay = `${exclusion.cashCurrency} ${this.formatNumber(exclusion.cashAmount)}`;
      } else if (exclusion.excludeMarketValue) {
        formatted.marketValueDisplay = 'Exclude Market Value';
      } else {
        formatted.marketValueDisplay = `${exclusion.currency} ${this.formatNumber(exclusion.marketValue)}`;
      }

      // Date displays
      formatted.fromDateDisplay = this.convertToLocaleTime(exclusion.fromDate);
      formatted.toDateDisplay = exclusion.toDate 
        ? this.convertToLocaleTime(exclusion.toDate) 
        : '<i class="fas fa-infinity"></i>';

      return formatted;
    });
  };

  formatNumber = (value: number): string => {
    if (value === null || value === undefined) return '';
    return value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  addFeeExclusion = () => {
    if (!this.canEdit) return;

    const tempScope = this.$rootScope.$new(true) as any;
    tempScope.portfolios = (this.client.portfolios || []).map((p: any) => ({
      portfolioId: p.portfolioId,
      portfolioName: p.name,
      defaultCurrency: this.portfolioDefaults[p.portfolioId] || null,
    }));

    this.$mdDialog
      .show({
        template: `<md-dialog flex="30"><create-portfolio-fee-exclusion-dialog portfolios="portfolios"></create-portfolio-fee-exclusion-dialog></md-dialog>`,
        clickOutsideToClose: true,
        scope: tempScope,
      })
      .then((result) => {
        if (result) {
          this.getClientFeeExclusions();
        }
      });
  };

  editFeeExclusion = (portfolioFeeExclusion: any) => {
    if (!this.canEdit) return;

    const tempScope = this.$rootScope.$new(true) as any;
    tempScope.exclusion = portfolioFeeExclusion;

    this.$mdDialog
      .show(<any>{
        template: `<md-dialog flex="30"><edit-portfolio-fee-exclusion-dialog exclusion="exclusion"></edit-portfolio-fee-exclusion-dialog></md-dialog>`,
        clickOutsideToClose: true,
        scope: tempScope,
        minWidth: '500px',
      })
      .then((result) => {
        if (result) {
          this.getClientFeeExclusions();
          if (this.onRefresh) {
            this.onRefresh();
          }
        }
      });
  };

  getTitle = (portfolioFeeExclusion: any): string => {
    return portfolioFeeExclusion.modifiedBy
      ? `Modified on ${new Date(portfolioFeeExclusion.dateModified)
          .toISOString()
          .substr(0, 10)} by ${portfolioFeeExclusion.modifiedBy}`
      : "";
  };

  isSeniorManager = (): boolean => {
    const userRoles = this.authService.getUserRoles();
    if (!userRoles) return false;
    return (
      userRoles.indexOf(Roles.Accountant) > -1 ||
      userRoles.indexOf(Roles.SystemAdministrator) > -1
    );
  };
}

export const ExclusionsComponent = {
  template: require('./exclusions.html'),
  controller: ExclusionsController,
  controllerAs: '$ctrl',
  bindings: {
    client: '<',
    onRefresh: '&', // Called to refresh the data
    canEdit: '<' // Whether user can edit exclusions
  }
};




// WEBPACK FOOTER //
// ./src/app/fees/exclusions/exclusions.ts